import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import AirportConveyanceModal from "./AirportConveyanceModal";
import GuestBookingModal from "./GuestBookingModal";
import DatePicker from "react-datepicker";
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { useGoogleMaps } from "../../context/GoogleMapsContext";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./InformationCard.module.css";
import { FaUsers, FaWifi, FaBed, FaBath, FaSnowflake, FaMapMarkerAlt } from "react-icons/fa";
import { MdTv } from "react-icons/md";
import { useUser } from "../../context/UserContext"; // <-- Import user context
import api from "../../api/axois"; // <-- Import API instance
import { openWhatsApp, buildBookingMessage } from "../../utils/whatsappUtils";


const InformationCard = ({ display, onBook }) => {
  // Policies modal hooks (must be inside component)
  const [showPoliciesModal, setShowPoliciesModal] = useState(false);
  const policiesModalRef = useRef(null);
  const [policiesText, setPoliciesText] = useState("");
  useEffect(() => {
    // Try to fetch policies targeted for the Information Card
    (async () => {
      try {
        const res = await api.get('/policies', { params: { placement: 'information_card' } });
        const list = res?.data?.policies || [];
        if (list.length > 0) {
          // Join multiple policies' contents with a divider
          const joined = list.map(p => p.content).join('\n\n');
          setPoliciesText(joined);
        }
      } catch (e) {
        // Fallback to default text on error
        // setPoliciesText(DEFAULT_POLICIES_TEXT);
      }
    })();
  }, []);
  useEffect(() => {
    if (!showPoliciesModal) return;
    function handleClickOutside(event) {
      if (policiesModalRef.current && !policiesModalRef.current.contains(event.target)) {
        setShowPoliciesModal(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPoliciesModal]);

  const { user } = useUser(); // <-- Get user from context
  const { isLoaded, loadError } = useGoogleMaps(); // <-- Get Google Maps status
  const [showConveyanceModal, setShowConveyanceModal] = useState(false);
  const [hasConveyance, setHasConveyance] = useState(false);
  const [conveyanceData, setConveyanceData] = useState(null);
  // Use backend values for breakfast and conveyance as defaults
  const [hasBreakfast, setHasBreakfast] = useState(!!display?.breakfast_enabled);
  const [breakfastCount, setBreakfastCount] = useState(1);
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [guestInfo, setGuestInfo] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingSuccess, setBookingSuccess] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  // Affiliate code
  const location = useLocation();
  const [affiliateCode, setAffiliateCode] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  // POI state
  const [mapRef, setMapRef] = useState(null);
  const [poi, setPoi] = useState([]);
  const [poiLoading, setPoiLoading] = useState(false);
  const [poiError, setPoiError] = useState("");
  const [poiType, setPoiType] = useState("restaurant");
  const [poiRadius, setPoiRadius] = useState(1500);
  const [activePoi, setActivePoi] = useState(null);

  const userIsLoggedIn = !!user; // <-- Check if user is logged in
  const [supportPhone, setSupportPhone] = useState("");
  useEffect(() => {
    api.get("/app-settings/public").then(res => {
      setSupportPhone(res?.data?.settings?.support_phone || "");
    }).catch(() => {});
  }, []);
  // Pre-fill affiliate code from URL or saved storage
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const code = sp.get("aff") || sp.get("affiliate") || sp.get("ref") || sp.get("code");
      if (code) {
        setAffiliateCode(code);
        try { localStorage.setItem("affiliate_code", code); } catch {}
      } else {
        // fallback to saved
        if (!affiliateCode) {
          const saved = localStorage.getItem("affiliate_code");
          if (saved) setAffiliateCode(saved);
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const code = (sp.get("promo") || "").trim();
      if (code) {
        const normalized = code.toUpperCase();
        setPromoCode(normalized);
        try { localStorage.setItem("promo_code", normalized); } catch {}
      } else {
        const saved = localStorage.getItem("promo_code");
        if (saved) setPromoCode(saved.toUpperCase());
      }
    } catch {}
  }, [location.search]);

  useEffect(() => {
    if (!promoCode) {
      setPromoInfo(null);
      return;
    }

    let isMounted = true;
    api.get(`/promotions/resolve/${encodeURIComponent(promoCode)}`)
      .then((res) => {
        if (!isMounted) return;
        const data = res?.data?.promotion || null;
        const currentRoomTypeId = Number(display?.room_type_id || display?.id);
        if (data && Number(data.room_type_id) === currentRoomTypeId) {
          setPromoInfo(data);
        } else {
          setPromoInfo(null);
        }
      })
      .catch(() => {
        if (isMounted) setPromoInfo(null);
      });

    return () => {
      isMounted = false;
    };
  }, [promoCode, display?.room_type_id, display?.id]);


  const formatDate = (date) =>
    date?.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  // Determine max breakfast count based on room type
  const getMaxBreakfastCount = () => {
    const title = display?.title?.toLowerCase() || '';
    if (title.includes('2 bedroom') || title.includes('two bedroom')) return 4;
    return 2;
  };

  const handleGuestSubmit = async (guestData) => {
    try {
      setShowGuestModal(false); // Hide guest modal immediately
      setRedirecting(true);
      // Wait 2 seconds before calling payment API
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const formatDateForBackend = (date) => {
        if (!date) return null;
        const d = date instanceof Date ? date : new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const payload = {
        room_type_id: display.room_type_id || display.id,
        start_date: formatDateForBackend(startDate),
        end_date: formatDateForBackend(endDate),
        airport_conveyance: hasConveyance ? {
          direction: conveyanceData?.direction,
          pickup: conveyanceData?.pickup || null,
          dropoff: conveyanceData?.dropoff || null,
        } : null,
        breakfast: !!hasBreakfast,
        ...(hasBreakfast ? { breakfast_count: breakfastCount } : {}),
        ...(affiliateCode ? { affiliate_code: affiliateCode.trim() } : {}),
        ...(promoCode ? { promo_code: promoCode.trim() } : {}),
        guest_name: guestData.name,
        guest_email: guestData.email,
        guest_phone: guestData.phone,
        // New info from backend
        breakfast_enabled: display.breakfast_enabled,
        breakfast_price: display.breakfast_price,
        airport_conveyance_enabled: display.airport_conveyance_enabled,
        airport_conveyance_price: display.airport_conveyance_price,
      };

      setIsBooking(true);
      const res = await api.post("/initiate-payment", payload);
      
      if (res.data.authorization_url) {
        setTimeout(() => {
          window.location.href = res.data.authorization_url;
        }, 500); // Short delay for smooth transition
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (err) {
      setBookingError("Failed to initiate payment. Please try again.");
      throw err; // Propagate error to modal
    } finally {
      setIsBooking(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const newErrors = {};
    if (!startDate || !endDate) {
      newErrors.dates = "Please select a date range.";
    }
    if (!agreedToTerms) {
      newErrors.terms = "You must agree to the terms.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    // If user is not logged in and we don't have guest info yet
    if (!userIsLoggedIn && !guestInfo) {
      setShowGuestModal(true);
      return;
    }

    // If logged in user, proceed with payment
    if (userIsLoggedIn || guestInfo) {
      setRedirecting(true);
      setIsBooking(true);
      
      // Wait 2 seconds before calling payment API
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const formatDateForBackend = (date) => {
          if (!date) return null;
          const d = date instanceof Date ? date : new Date(date);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const payload = {
          room_type_id: display.room_type_id || display.id,
          start_date: formatDateForBackend(startDate),
          end_date: formatDateForBackend(endDate),
          airport_conveyance: hasConveyance ? {
            direction: conveyanceData?.direction,
            pickup: conveyanceData?.pickup || null,
            dropoff: conveyanceData?.dropoff || null,
          } : null,
          breakfast: !!hasBreakfast,
          ...(hasBreakfast ? { breakfast_count: breakfastCount } : {}),
          ...(guestInfo && {
            guest_name: guestInfo.name,
            guest_email: guestInfo.email,
            guest_phone: guestInfo.phone,
          }),
          ...(affiliateCode ? { affiliate_code: affiliateCode.trim() } : {}),
          ...(promoCode ? { promo_code: promoCode.trim() } : {}),
          // New info from backend
          breakfast_enabled: display.breakfast_enabled,
          breakfast_price: display.breakfast_price,
          airport_conveyance_enabled: display.airport_conveyance_enabled,
          airport_conveyance_price: display.airport_conveyance_price,
        };
        const res = await api.post("/initiate-payment", payload);

        if (res.data.authorization_url) {
          setRedirecting(true);
          setTimeout(() => {
            window.location.href = res.data.authorization_url;
          }, 2000);
        } else {
          throw new Error('No authorization URL received');
        }
      } catch (err) {
        setBookingError("Failed to initiate payment. Please try again.");
      } finally {
        setIsBooking(false);
        setGuestInfo(null); // Reset guest info after booking
      }
    }
  };

  const isFormValid = startDate && endDate && agreedToTerms;

  // Helper function to get valid coordinates
  const getValidCoordinates = () => {
    const lat = typeof display?.latitude === 'number' ? display.latitude : parseFloat(display?.latitude);
    const lng = typeof display?.longitude === 'number' ? display.longitude : parseFloat(display?.longitude);
    const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90;
    const isValidLng = !isNaN(lng) && lng >= -180 && lng <= 180;
    return {
      lat: isValidLat ? lat : 9.0765,
      lng: isValidLng ? lng : 7.3986
    };
  };

  const coordinates = getValidCoordinates();

  // Use display prop if provided, otherwise fallback to defaults
  const title = display?.title || "1 Bedroom Studio Apartment";
  const price = display?.price_per_night
    ? `₦${Number(display.price_per_night).toLocaleString()} Per Night`
    : "₦450,000.00 Per Month";
  const availableCount = display?.available_count !== undefined ? display.available_count : 4;
  const available = `${availableCount} AVAILABLE`;
  const isAvailable = availableCount > 0;

  // Disable date selection if no rooms available
  const canSelectDates = isAvailable;
  // Show full description (no truncation)
  const description = display?.description ||
    "A cozy yet stylish space featuring a king-size bed, modern furnishings, and a private en-suite bathroom with premium toiletries. Enjoy high-speed Wi-Fi, a smart TV, and 24/7 room service for a seamless stay. Perfect for both leisure and business travelers.";
  const capacity = display?.capacity || 2;

  // Calculate pricing breakdown
  const calculatePricing = () => {
    const nightlyRate = display?.price_per_night || 0;
    let totalNights = 0;
    let roomTotal = 0;
    let conveyanceTotal = 0;
    let breakfastTotal = 0;

    if (startDate && endDate) {
      const diffTime = Math.abs(endDate - startDate);
      totalNights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      roomTotal = nightlyRate * totalNights;
    }

    if (hasConveyance && conveyanceData) {
      const isFreeConveyance = totalNights >= 10;
      if (!isFreeConveyance) {
        if (conveyanceData.direction === 'both') {
          conveyanceTotal = display?.airport_both_price || 70000;
        } else if (conveyanceData.direction === 'pickup') {
          conveyanceTotal = display?.airport_pickup_price || 40000;
        } else if (conveyanceData.direction === 'dropoff') {
          conveyanceTotal = display?.airport_dropoff_price || 40000;
        }
      }
    }

    if (hasBreakfast && totalNights > 0) {
      const breakfastRate = display?.breakfast_price || 0;
      breakfastTotal = breakfastRate * breakfastCount * totalNights;
    }

    const grandTotal = roomTotal + conveyanceTotal + breakfastTotal;

    return {
      totalNights,
      roomTotal,
      conveyanceTotal,
      breakfastTotal,
      grandTotal,
      nightlyRate
    };
  };

  const pricing = calculatePricing();
  const estimatedPromoDiscount = promoInfo ? Math.round((pricing.grandTotal * Number(promoInfo.percentage || 0)) / 100) : 0;
  const estimatedTotalAfterPromo = Math.max(0, pricing.grandTotal - estimatedPromoDiscount);



  return (
    <>
      {redirecting && (
        <div className={styles.redirectModalOverlay}>
          <div className={styles.redirectModalPopup}>
            <button className={styles.redirectCloseBtn} onClick={() => setRedirecting(false)}>
              ×
            </button>
            <h3 style={{marginTop:0}}>You are about to be redirected to a payment portal...</h3>
            <p>Please wait while we connect you to Paystack.</p>
          </div>
        </div>
      )}
      <form 
        className={styles.infoCard} 
        onSubmit={handleSubmit}
        noValidate // Add this to prevent default HTML5 validation
      >
      {/* ...existing code... */}
      <div className={styles.infoMap}>
        {/* <h3><FaMapMarkerAlt /> Location</h3> */}
        <div className={styles.mapContainer}>
          {import.meta.env.VITE_GOOGLE_MAPS_API_KEY ? (
            <>
              {loadError && (
                <div style={{
                  width: '100%',
                  height: '200px',
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  color: '#666'
                }}>
                  <p>Error loading Google Maps</p>
                </div>
              )}
              {!isLoaded && !loadError && (
                <div style={{
                  width: '100%',
                  height: '200px',
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  color: '#666'
                }}>
                  <p>Loading Google Maps...</p>
                </div>
              )}
              {isLoaded && !loadError && (
                <>
                  <div className={styles.infoInputGroup}>
                    <label className={styles.inputLabel}>NEARBY</label>
                    <div className={styles.nearbyControls}>
                      <select className={styles.nearbySelect} value={poiType} onChange={e => setPoiType(e.target.value)}>
                      <option value="restaurant">Restaurants</option>
                      <option value="cafe">Cafes</option>
                      <option value="bar">Bars</option>
                      <option value="shopping_mall">Shopping</option>
                      <option value="hospital">Hospitals</option>
                      <option value="pharmacy">Pharmacies</option>
                      <option value="atm">ATMs</option>
                      <option value="bank">Banks</option>
                      <option value="gym">Gyms</option>
                      <option value="park">Parks</option>
                      <option value="tourist_attraction">Attractions</option>
                      </select>
                      <select className={styles.nearbySelect} value={poiRadius} onChange={e => setPoiRadius(Number(e.target.value))}>
                      <option value={500}>500m</option>
                      <option value={1000}>1km</option>
                      <option value={1500}>1.5km</option>
                      <option value={2000}>2km</option>
                      <option value={3000}>3km</option>
                      </select>
                      <button className={styles.nearbyButton} type="button" onClick={() => {
                      if (!mapRef) return;
                      const service = new window.google.maps.places.PlacesService(mapRef);
                      setPoiLoading(true);
                      setPoiError("");
                      const req = {
                        location: new window.google.maps.LatLng(coordinates.lat, coordinates.lng),
                        radius: poiRadius,
                        type: poiType
                      };
                      service.nearbySearch(req, (results, status) => {
                        setPoiLoading(false);
                        if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
                          setPoi([]);
                          setPoiError("No results for selected type/radius.");
                          return;
                        }
                        setPoi(results || []);
                      });
                      }}>Search</button>
                      {poiLoading && <span className={styles.nearbyStatus}>Loading…</span>}
                      {poiError && <span className={`${styles.nearbyStatus} ${styles.nearbyError}`}>{poiError}</span>}
                    </div>
                  </div>
                  <GoogleMap
                    mapContainerStyle={{
                      width: '100%',
                      height: '400px'
                    }}
                    center={coordinates}
                    zoom={15}
                    onLoad={(map) => setMapRef(map)}
                  >
                    <Marker
                      position={coordinates}
                      title={display?.title || "Room Location"}
                      icon={{
                        url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                      }}
                    />
                    {poi.map((place) => (
                      <Marker
                        key={place.place_id}
                        position={{
                          lat: place.geometry?.location?.lat(),
                          lng: place.geometry?.location?.lng()
                        }}
                        title={place.name}
                        onClick={() => setActivePoi(place)}
                      />
                    ))}
                    {activePoi && (
                      <InfoWindow
                        position={{
                          lat: activePoi.geometry?.location?.lat(),
                          lng: activePoi.geometry?.location?.lng()
                        }}
                        onCloseClick={() => setActivePoi(null)}
                      >
                        <div style={{ maxWidth: 220 }}>
                          <strong>{activePoi.name}</strong>
                          {activePoi.vicinity && <div>{activePoi.vicinity}</div>}
                          {typeof activePoi.rating === 'number' && (
                            <div>Rating: {activePoi.rating} ({activePoi.user_ratings_total || 0})</div>
                          )}
                        </div>
                      </InfoWindow>
                    )}
                  </GoogleMap>
                </>
              )}
            </>
          ) : (
            <div style={{
              width: '100%',
              height: '200px',
              backgroundColor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              color: '#666'
            }}>
              <p>Google Maps API key not configured</p>
            </div>
          )}
        </div>
      </div>
      <h2 className={styles.infoTitle}>{title}</h2>
      <p className={styles.infoPrice}>{price}</p>
      <p className={`${styles.infoAvailable} ${isAvailable ? styles.available : styles.unavailable}`}>
        {available}
      </p>

      <p className={styles.infoDescription}>{description}</p>

      <ul className={styles.infoFeaturesGrid}>
        <li>
          <FaUsers size={16} /> {capacity} Guests
        </li>
        {display?.wifi && (
          <li>
            <FaWifi size={16} /> Wi-Fi
          </li>
        )}
        {display?.king_size_bed && (
          <li>
            <FaBed size={16} /> King-size bed
          </li>
        )}
        {display?.private_bathroom && (
          <li>
            <FaBath size={16} /> Private bathroom
          </li>
        )}
        {display?.smart_tv && (
          <li>
            <MdTv size={16} /> Smart TV
          </li>
        )}
        {display?.air_conditioning && (
          <li>
            <FaSnowflake size={16} /> Air conditioning
          </li>
        )}
      </ul>

      

      <p className={styles.infoSubtext}>
        Best for solo travelers or budget-conscious guests
      </p>

      <div className={styles.infoFlexRow}>
        <div className={`${styles.infoInputGroup} ${styles.datePickerGroup}`}>
          <label className={styles.inputLabel}>DATES</label>
          <input
            readOnly
            onClick={() => {
              if (canSelectDates) setShowCalendar(!showCalendar);
            }}
            value={
              startDate && endDate
                ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                : canSelectDates ? "Select date range" : "No rooms available for selected dates"
            }
            disabled={!canSelectDates}
            style={!canSelectDates ? { background: '#f8d7da', color: '#a94442', cursor: 'not-allowed' } : {}}
          />
          {errors.dates && <p className={styles.formError}>{errors.dates}</p>}

          {showCalendar && canSelectDates && (
            <div className={styles.calendarWrapper}>
              <DatePicker
                onChange={(dates) => {
                  if (Array.isArray(dates)) {
                    const [start, end] = dates;
                    setStartDate(start);
                    setEndDate(end);
                  }
                }}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                inline
                minDate={new Date()}
              />
              <button
                className={styles.calendarConfirmBtn}
                type="button"
                onClick={() => setShowCalendar(false)}
              >
                Confirm
              </button>
            </div>
          )}
        </div>

        {display?.airport_conveyance_enabled && (
          <div className={styles.infoInputGroup}>
            <div className={styles.conveyanceToggle}>
              <label className={styles.inputLabel}>AIRPORT CONVEYANCE (Optional)</label>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={hasConveyance}
                  onChange={() => {
                    if (!hasConveyance) {
                      setShowConveyanceModal(true);
                    } else {
                      setHasConveyance(false);
                      setConveyanceData(null);
                    }
                  }}
                />
                <span className={`${styles.slider} ${styles.round}`}></span>
              </label>
            </div>
            {conveyanceData ? (
              <div className={styles.conveyanceSummary}>
                <span>
                  <span className={styles.value}>
                    {conveyanceData.direction === 'both' ? 'Pickup & Drop-off' : 
                     conveyanceData.direction === 'pickup' ? 'Pickup Only' : 'Drop-off Only'}
                  </span>
                </span>
                {conveyanceData.pickup && (
                  <span><span>Pickup:</span><span className={styles.value}>{conveyanceData.pickup.date} at {conveyanceData.pickup.time}</span></span>
                )}
                {conveyanceData.dropoff && (
                  <span><span>Drop-off:</span><span className={styles.value}>{conveyanceData.dropoff.date} at {conveyanceData.dropoff.time}</span></span>
                )}
              </div>
            ) : (
              <input readOnly value="None" />
            )}
          </div>
        )}

        {display?.breakfast_enabled && (
          <div className={styles.infoInputGroup}>
            <div className={styles.conveyanceToggle}>
              <label className={styles.inputLabel}>BREAKFAST (Optional)</label>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={hasBreakfast}
                  onChange={(e) => setHasBreakfast(e.target.checked)}
                />
                <span className={`${styles.slider} ${styles.round}`}></span>
              </label>
            </div>
            {hasBreakfast ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label htmlFor="breakfastCount" style={{ marginRight: 4 }}>For</label>
                <select
                  id="breakfastCount"
                  value={breakfastCount}
                  onChange={e => setBreakfastCount(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginBottom: '8px',
                    border: '1px solid #ccc',
                    fontFamily: 'Archivo, sans-serif',
                    borderRadius: '4px',
                    fontSize: '15px',
                    background: '#fff',
                  }}
                >
                  {Array.from({ length: getMaxBreakfastCount() }, (_, i) => i + 1).map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <span>person{breakfastCount > 1 ? 's' : ''}</span>
              </div>
            ) : (
              <input readOnly value="None" />
            )}
          </div>
        )}
        {/* Affiliate Code */}
        <div className={styles.infoInputGroup}>
          <label className={styles.inputLabel}>AFFILIATE CODE (Optional)</label>
          <input
            type="text"
            value={affiliateCode}
            placeholder="Enter affiliate/referral code"
            onChange={(e) => {
              const val = e.target.value;
              setAffiliateCode(val);
              try { localStorage.setItem("affiliate_code", val); } catch {}
            }}
          />
        </div>
      </div>

      {/* Pricing Breakdown */}
      {(startDate && endDate) && (
        <div className={styles.pricingBreakdown}>
          <h3 className={styles.breakdownTitle}>Pricing Summary</h3>
          
          <div className={`${styles.breakdownItem} ${styles.fadeIn}`}>
            <span>Room ({pricing.totalNights} night{pricing.totalNights !== 1 ? 's' : ''}):</span>
            <span>₦{pricing.roomTotal.toLocaleString()}</span>
          </div>

          {hasConveyance && conveyanceData && (
            <div className={`${styles.breakdownItem} ${styles.fadeIn}`}>
              <span>
                Airport {conveyanceData.direction === 'both' ? 'Pickup & Drop-off' : 
                        conveyanceData.direction === 'pickup' ? 'Pickup' : 'Drop-off'}:
              </span>
              <span>
                {pricing.totalNights >= 10 ? (
                  <span className={styles.freeTag}>FREE</span>
                ) : (
                  `₦${pricing.conveyanceTotal.toLocaleString()}`
                )}
              </span>
            </div>
          )}

          {hasBreakfast && (
            <div className={`${styles.breakdownItem} ${styles.fadeIn}`}>
              <span>Breakfast ({breakfastCount} person{breakfastCount > 1 ? 's' : ''} × {pricing.totalNights} night{pricing.totalNights !== 1 ? 's' : ''}):</span>
              <span>₦{pricing.breakfastTotal.toLocaleString()}</span>
            </div>
          )}

          {promoInfo && estimatedPromoDiscount > 0 && (
            <>
              <div className={`${styles.breakdownItem} ${styles.fadeIn}`}>
                <span>Promo ({promoInfo.percentage}% off):</span>
                <span>- ₦{estimatedPromoDiscount.toLocaleString()}</span>
              </div>
              <div className={`${styles.breakdownItem} ${styles.fadeIn}`}>
                <span>Promo code:</span>
                <span>{promoCode}</span>
              </div>
            </>
          )}

          <div className={styles.breakdownDivider}></div>
          <div className={`${styles.breakdownItem} ${styles.totalRow} ${styles.fadeIn}`}>
            <span>{promoInfo && estimatedPromoDiscount > 0 ? 'Estimated Total After Promo:' : 'Total:'}</span>
            <span>₦{(promoInfo && estimatedPromoDiscount > 0 ? estimatedTotalAfterPromo : pricing.grandTotal).toLocaleString()}</span>
          </div>
        </div>
      )}

      <button 
        type="submit" 
        className={styles.guestBtn} 
        disabled={!isFormValid || isBooking}
      >
        {isBooking ? "Processing..." : "BOOK NOW"}
      </button>

      {supportPhone && (
        <button
          type="button"
          className={styles.whatsappBtn}
          onClick={() => openWhatsApp(supportPhone, buildBookingMessage({ roomType: display?.title || display?.roomType?.title || "" }))}
        >
          Book via WhatsApp
        </button>
      )}

      {bookingError && (
        <div className={styles.errorMessage}>{bookingError}</div>
      )}

      <div className={styles.policyCheck}>
        <input
          type="checkbox"
          id="terms"
          checked={agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
        />
        <label htmlFor="terms">
          I agree to the Terms of Stay and Residency Policies
        </label>
      </div>
      {errors.terms && <p className={styles.formError}>{errors.terms}</p>}
      <p className={styles.policyLink} style={{cursor:'pointer'}} onClick={() => setShowPoliciesModal(true)}>[ View Policies ]</p>

      {showPoliciesModal && (
        <div className={styles.policiesModalOverlay}>
          <div className={styles.policiesModal} ref={policiesModalRef}>
            <button className={styles.policiesModalClose} onClick={() => setShowPoliciesModal(false)}>&times;</button>
            <h2 className={styles.policiesModalTitle}>Residency Policies</h2>
            <div className={styles.policiesModalContent}>
              {policiesText.split('\n').map((line, idx) => (
                <p key={idx} style={{margin:0}}>{line}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      <AirportConveyanceModal
        isOpen={showConveyanceModal}
        onClose={() => setShowConveyanceModal(false)}
        onConfirm={(data) => {
          setConveyanceData(data);
          setHasConveyance(true);
          setShowConveyanceModal(false);
        }}
      />

      <GuestBookingModal
        isOpen={showGuestModal}
        onClose={() => setShowGuestModal(false)}
        onGuestSubmit={handleGuestSubmit}
      />
      </form>
    </>
  );
};

export default InformationCard;
